import {
  Fragment,
  Identifier,
  RawSQL,
  sqlF,
  sqlIdentifier,
  sqlJoinF,
} from './sqlTemplate';

/**
 * A list-building helper for creating SQL fragments. Mapping, concatenating,
 * joining, etc. can be some of the most difficult-to-read parts of SQL template
 * code; but that's also a lot of what we do when generating SQL with code. The
 * goal of this class is to make it a bit easier to generate and read generated
 * SQL lists.
 *
 * Typically, you'll create a list with `sqlList()` or `sqlIdList()`, optionally
 * passing in some initial values. Then you'll add items. Then you'll join them
 * to output one RawSQL fragment that can be used in a template.
 *
 * For example, instead of this:
 *
 * sqlJoinF(
 *   myList.map((item) => sqlF`${sqlIdentifier(item)}`),
 *  ', '
 * )
 *
 * You can write this:
 *
 * sqlIdList(myList).commaJoin()
 */
export class SqlList {
  private parts = new Array<RawSQL>();

  constructor(input?: Iterable<Fragment | SqlList>) {
    for (const item of input ?? []) {
      this.add(item);
    }
  }

  get rawParts(): ReadonlyArray<RawSQL> {
    return this.parts;
  }

  add(input: Fragment | SqlList): SqlList {
    if (input instanceof SqlList) {
      for (const p of input.rawParts) {
        this.parts.push(p);
      }
    } else {
      this.parts.push(sqlF`${input}`);
    }
    return this;
  }

  size(): number {
    return this.parts.length;
  }

  addIter(input: Iterable<Fragment>): SqlList {
    for (const item of input) {
      this.add(item);
    }
    return this;
  }

  addMapped<T extends any>(
    input: Iterable<T>,
    transform: (x: T) => RawSQL
  ): SqlList {
    for (const item of input) {
      this.add(transform(item));
    }
    return this;
  }

  addId(input: string): SqlList {
    this.add(sqlIdentifier(input));
    return this;
  }

  addIds(
    input: Iterable<string>,
    transform?: (id: Identifier) => RawSQL
  ): SqlList {
    for (const item of input) {
      if (transform) {
        this.add(transform(sqlIdentifier(item)));
      } else {
        this.addId(item);
      }
    }
    return this;
  }

  /**
   * Joins the list with the given glue.
   * Kept private to ensure we're always explicit about what kind of join we're doing.
   */
  private join(glue: string): RawSQL {
    return sqlJoinF(this.parts, glue);
  }

  commaJoin(): RawSQL {
    // eslint-disable-next-line @watershed/no-join-commas
    return this.join(', ');
  }

  commaNewlineJoin(): RawSQL {
    return this.join(',\n');
  }

  newlineJoin(): RawSQL {
    return this.join('\n');
  }

  semicolonJoin(): RawSQL {
    return this.join('; ');
  }

  andJoin(): RawSQL {
    return this.join(' AND ');
  }

  orJoin(): RawSQL {
    return this.join(' OR ');
  }

  unionAllJoin(): RawSQL {
    return this.join(' UNION ALL ');
  }

  unionAllByNameJoin(): RawSQL {
    return this.join(' UNION ALL BY NAME ');
  }
}

export function sqlList(input?: Iterable<Fragment>): SqlList {
  return new SqlList(input);
}

export function sqlIdList(
  input: Iterable<string>,
  transform?: (id: Identifier) => RawSQL
): SqlList {
  return sqlList().addIds(input, transform);
}
